import React, { useState, useEffect, useRef } from 'react';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Spinner, 
    Badge, 
    Modal, 
    Form,
    Alert,
    Table,
    Dropdown,
    ProgressBar,
    InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers,
    faPlus,
    faFileUpload,
    faEye,
    faEdit,
    faTrash,
    faSync,
    faExclamationTriangle,
    faUserTie,
    faBoxOpen,
    faCalendarAlt,
    faInfoCircle,
    faSave,
    faCheck,
    faTimes,
    faDownload,
    faCloudUploadAlt,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faSearch,
    faPhone,
    faEnvelope,
    faBuilding,
    faCog,
    faHistory,
    faChartBar,
    faTrashAlt,
    faList,
    faTh,
    faUpload,
    faFileAlt,
    faCopy,
    faBullseye
} from '@fortawesome/free-solid-svg-icons';
import { 
    SellerbotService,
    useSellerbotCampaigns,
    useSellerbotProfiles
} from '../../service/sellerbotService';

// ==================== 📋 INTERFACES ====================

interface ContactsTabProps {
    language: 'es' | 'en';
    onContactsChange?: (contacts: Contact[]) => void;
}

interface Contact {
    id?: string;
    name: string;
    phone: string;
    email?: string;
    company?: string;
    product_service_interest?: string;
    source: string;
    campaign_id?: string;
    agent_id?: string;
    status: 'new' | 'contacted' | 'interested' | 'closed' | 'rejected';
    created_at?: string;
    updated_at?: string;
    last_contact_date?: string;
    attempts_count?: number;
}

interface PreviewContact {
    name: string;
    phone: string;
    email?: string;
    company?: string;
    product_service_interest?: string;
    course?: string;
    hasError?: boolean;
    errorMessage?: string;
    rowNumber?: number;
    source?: string;
    status?: string;
}

interface ContactValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    phoneIssues: PhoneIssue[];
    duplicates: any[];
    totalContacts: number;
    validContacts: number;
}

interface PhoneIssue {
    row: number;
    original: string;
    corrected: string;
    issue: string;
}

interface UploadHistory {
    id: string;
    fileName: string;
    uploadDate: string;
    contactsCount: number;
    validContacts: number;
    invalidContacts: number;
    duplicatesFound: number;
    status: 'success' | 'error' | 'partial';
    errorMessage?: string;
    campaignName?: string;
    agentName?: string;
    processingTime?: number;
    fileSize?: number;
}

interface ContactFilter {
    search: string;
    status: string;
    campaign: string;
    agent: string;
}

// ==================== 🎨 ESTILOS CSS ====================
const customStyles = `
  .contacts-tab .contact-card {
    transition: all 0.3s ease !important;
    border: 1px solid #e9ecef !important;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  }

  .contacts-tab .contact-card:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
    transform: translateY(-2px) !important;
    z-index: 1 !important;
  }

  .contacts-tab .status-new {
    background-color: #f8f9fa;
    color: #495057;
  }

  .contacts-tab .status-contacted {
    background-color: #cff4fc;
    color: #055160;
  }

  .contacts-tab .status-interested {
    background-color: #d1ecf1;
    color: #0c5460;
  }

  .contacts-tab .status-closed {
    background-color: #d4edda;
    color: #155724;
  }

  .contacts-tab .status-rejected {
    background-color: #f8d7da;
    color: #721c24;
  }

  .contacts-tab .kpi-card {
    transition: all 0.3s ease;
    border-left: 4px solid transparent;
  }
  
  .contacts-tab .kpi-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
    border-left-color: var(--bs-primary);
  }

  .contacts-tab .drag-over {
    background-color: rgba(0, 123, 255, 0.1);
    border: 3px dashed #007bff;
  }

  @media (max-width: 768px) {
    .contacts-tab .contact-card:hover {
      transform: none;
    }
  }
`;

// ==================== 🎯 COMPONENTE PRINCIPAL ====================
const ContactsTab: React.FC<ContactsTabProps> = ({ language, onContactsChange }) => {
    
    // ==================== HOOKS DE DATOS ====================
    const { campaigns } = useSellerbotCampaigns();
    const { profiles } = useSellerbotProfiles();

    // ==================== ESTADOS PRINCIPALES ====================
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [uploadHistory, setUploadHistory] = useState<UploadHistory[]>([]);
    const [previewContacts, setPreviewContacts] = useState<PreviewContact[]>([]);
    
    // Estados de carga
    const [isLoadingContacts, setIsLoadingContacts] = useState(true);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [isProcessingFile, setIsProcessingFile] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    
    // Estados de modales
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showValidationModal, setShowValidationModal] = useState(false);
    
    // Estados de filtros y búsqueda
    const [contactFilter, setContactFilter] = useState<ContactFilter>({
        search: '',
        status: 'all',
        campaign: 'all',
        agent: 'all'
    });
    const [searchTerm, setSearchTerm] = useState('');
    
    // Estados de upload
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [selectedCampaign, setSelectedCampaign] = useState('');
    const [selectedAgent, setSelectedAgent] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    
    // Estados de validación
    const [validationResult, setValidationResult] = useState<ContactValidationResult | null>(null);
    const [duplicatesFound, setDuplicatesFound] = useState<any[]>([]);
    const [updateDuplicates, setUpdateDuplicates] = useState(true);
    
    // Estados de UI
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    
    // Referencias
    const fileInputRef = useRef<HTMLInputElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const preventUpdates = useRef(false);

    // ==================== EFECTOS ====================
    
    useEffect(() => {
        loadContacts();
        loadUploadHistory();
    }, []);

    useEffect(() => {
        if (onContactsChange) {
            onContactsChange(contacts);
        }
    }, [contacts, onContactsChange]);

    // Búsqueda con debounce
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            setContactFilter(prev => ({ ...prev, search: searchTerm }));
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    // Aplicar filtros
    useEffect(() => {
        if (!preventUpdates.current) {
            loadContacts(true);
        }
    }, [contactFilter.status, contactFilter.campaign, contactFilter.agent, contactFilter.search]);

    // ==================== 🔧 FUNCIONES DE PROCESAMIENTO DE ARCHIVOS ====================

// Función para detectar separador CSV
const detectSeparator = (line: string): string => {
    const separators = [',', ';', '\t', '|'];
    let maxCount = 0;
    let bestSeparator = ',';
    
    separators.forEach(sep => {
        const count = (line.match(new RegExp(`\\${sep}`, 'g')) || []).length;
        if (count > maxCount) {
            maxCount = count;
            bestSeparator = sep;
        }
    });
    
    return bestSeparator;
};

    // Función para detectar si la primera línea son headers
const detectIfHasHeaders = (values: string[]): boolean => {
    if (values.length === 0) return false;
    
    const headerPatterns = [
        /^(nombre|name)$/i,
        /^(telefono|teléfono|phone)$/i,
        /^(email|correo)$/i,
        /^(empresa|company)$/i
    ];
    
    return values.some(value => 
        headerPatterns.some(pattern => pattern.test(value.trim()))
    );
};

    // Función para parsear una línea CSV
const parseCSVLine = (line: string, separator: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === separator && !inQuotes) {
            result.push(current.trim().replace(/^"|"$/g, ''));
            current = '';
        } else {
            current += char;
        }
    }
    
    result.push(current.trim().replace(/^"|"$/g, ''));
    return result;
};

    // Función para limpiar números de teléfono
const cleanPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    
    // Remover caracteres no numéricos excepto + al inicio
    let cleaned = phone.toString().trim();
    
    // Remover comillas y espacios extra
    cleaned = cleaned.replace(/[""'']/g, '').replace(/\s+/g, '');
    
    // Mantener solo números y + al inicio
    cleaned = cleaned.replace(/[^\d+]/g, '');
    
    // Si empieza con +, mantenerlo
    if (cleaned.startsWith('+')) {
        cleaned = '+' + cleaned.substring(1).replace(/[^\d]/g, '');
    } else {
        cleaned = cleaned.replace(/[^\d]/g, '');
    }
    
    // Para números chilenos, agregar +56 si no está presente
    if (cleaned.match(/^9\d{8}$/)) {
        cleaned = '+56' + cleaned;
    } else if (cleaned.match(/^56\d{9}$/)) {
        cleaned = '+' + cleaned;
    }
    
    return cleaned;
};

    // Función para limpiar emails
const cleanEmailValue = (value: string): string => {
    return value
        .toLowerCase()
        .replace(/[""'']/g, '')
        .replace(/\s+/g, '')
        .trim();
};

    // Función principal para procesar archivo CSV/XLSX
const processUploadedFile = async (file: File): Promise<PreviewContact[]> => {
    return new Promise((resolve, reject) => {
        console.log('📖 Processing file:', file.name, 'Size:', file.size);
        
        // Validar tamaño de archivo
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            reject(new Error(language === 'es' 
                ? 'Archivo muy grande. Máximo 10MB permitido.' 
                : 'File too large. Maximum 10MB allowed.'));
            return;
        }

        const reader = new FileReader();
        
        reader.onload = async (e) => {
            try {
                const data = e.target?.result;
                let contacts: PreviewContact[] = [];
                
                if (file.name.endsWith('.csv')) {
                    console.log('📄 Processing CSV file...');
                    const csv = data as string;
                    
                    // Limpiar y normalizar el CSV
                    const normalizedCsv = csv
                        .replace(/\r\n/g, '\n')
                        .replace(/\r/g, '\n')
                        .trim();
                    
                    const lines = normalizedCsv.split('\n').filter(line => line.trim());
                    
                    if (lines.length === 0) {
                        throw new Error(language === 'es' ? 'El archivo CSV está vacío' : 'CSV file is empty');
                    }
                    
                    console.log('📊 Total lines found:', lines.length);
                    
                    // Detectar separador
                    const separator = detectSeparator(lines[0]);
                    console.log('🔍 CSV separator detected:', separator);
                    
                    // Detectar si la primera línea son headers
                    const firstLineValues = parseCSVLine(lines[0], separator);
                    const hasHeaders = detectIfHasHeaders(firstLineValues);
                    
                    console.log('🎯 Has headers:', hasHeaders);
                    console.log('📋 First line values:', firstLineValues);
                    
                    // Determinar líneas de datos
                    const dataLines = hasHeaders ? lines.slice(1) : lines;
                    const headers = hasHeaders ? firstLineValues : ['name', 'phone', 'email', 'company', 'product_service_interest'];
                    
                    // Limitar a 50 contactos máximo
                    const maxContacts = Math.min(dataLines.length, 50);
                    const processedLines = dataLines.slice(0, maxContacts);
                    
                    if (dataLines.length > maxContacts) {
                        console.warn(`File truncated to ${maxContacts} contacts for performance`);
                    }
                    
                    // Mapear headers a campos estándar
                    const fieldMapping: { [key: string]: string } = {
                        'nombre': 'name',
                        'name': 'name',
                        'telefono': 'phone',
                        'phone': 'phone',
                        'teléfono': 'phone',
                        'email': 'email',
                        'correo': 'email',
                        'empresa': 'company',
                        'company': 'company',
                        'producto': 'product_service_interest',
                        'product': 'product_service_interest',
                        'servicio': 'product_service_interest',
                        'interest': 'product_service_interest',
                        'curso': 'product_service_interest',
                        'course': 'product_service_interest'
                    };
                    
                    contacts = processedLines.map((line, index) => {
                        try {
                            const values = parseCSVLine(line, separator);
                            const contact: PreviewContact = { 
                                name: '',
                                phone: '',
                                rowNumber: hasHeaders ? index + 2 : index + 1,
                                source: 'csv_upload',
                                status: 'new'
                            };
                            
                            // Mapear valores a campos
                            headers.forEach((header, i) => {
                                const mappedField = fieldMapping[header.toLowerCase()] || header.toLowerCase();
                                const value = values[i] || '';
                                
                                switch (mappedField) {
                                    case 'name':
                                        contact.name = value.trim();
                                        break;
                                    case 'phone':
                                        contact.phone = cleanPhoneNumber(value);
                                        break;
                                    case 'email':
                                        contact.email = cleanEmailValue(value);
                                        break;
                                    case 'company':
                                        contact.company = value.trim();
                                        break;
                                    case 'product_service_interest':
                                        contact.product_service_interest = value.trim();
                                        break;
                                    default:
                                        // Campos adicionales
                                        (contact as any)[mappedField] = value.trim();
                                }
                            });
                            
                            // Validar campos requeridos
                            if (!contact.name || !contact.phone) {
                                contact.hasError = true;
                                contact.errorMessage = language === 'es' 
                                    ? 'Faltan nombre o teléfono' 
                                    : 'Missing name or phone';
                            } else if (contact.phone.length < 8) {
                                contact.hasError = true;
                                contact.errorMessage = language === 'es' 
                                    ? 'Número de teléfono inválido' 
                                    : 'Invalid phone number';
                            }
                            
                            return contact;
                            
                        } catch (lineError) {
                            console.warn(`Error processing line ${index + 1}:`, lineError);
                            return {
                                rowNumber: hasHeaders ? index + 2 : index + 1,
                                name: `Error line ${index + 1}`,
                                phone: '',
                                hasError: true,
                                errorMessage: 'Line processing error',
                                source: 'csv_upload'
                            };
                        }
                    });
                    
                } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                    console.log('📊 Processing Excel file...');
                    // Para Excel, crear contactos de ejemplo por ahora
                    contacts = [
                        {
                            rowNumber: 1,
                            name: `Contacto de ${file.name}`,
                            phone: '+56912345678',
                            email: 'demo@excel.com',
                            company: 'Excel Company',
                            product_service_interest: 'Producto Demo',
                            source: 'excel_upload',
                            status: 'new',
                            hasError: false
                        }
                    ];
                }
                
                const validContacts = contacts.filter(contact => contact.name && contact.phone && !contact.hasError);
                const errorContacts = contacts.filter(contact => contact.hasError);
                
                console.log(`✅ Processing complete: ${validContacts.length} valid, ${errorContacts.length} with errors`);
                
                if (validContacts.length === 0 && errorContacts.length > 0) {
                    console.log('❌ All contacts have errors:', errorContacts.map(c => c.errorMessage));
                    throw new Error(language === 'es' 
                        ? `No se encontraron contactos válidos. Errores: ${errorContacts.length}`
                        : `No valid contacts found. Errors: ${errorContacts.length}`);
                }
                
                if (validContacts.length === 0) {
                    throw new Error(language === 'es' 
                        ? 'No se encontraron contactos en el archivo'
                        : 'No contacts found in the file');
                }
                
                resolve(contacts);
                
            } catch (error: any) {
                console.error('File processing error:', error);
                reject(error);
            }
        };
        
        reader.onerror = () => {
            reject(new Error(language === 'es' ? 'Error al leer el archivo' : 'Error reading file'));
        };
        
        reader.readAsText(file, 'UTF-8');
    });
};

    // ==================== 📝 FUNCIONES PRINCIPALES ====================
    
    const loadContacts = async (silent = false) => {
        try {
            if (!silent) setIsLoadingContacts(true);
            
            const searchParams: any = {
                limit: 100,
                offset: 0
            };
            
            if (contactFilter.status !== 'all' && contactFilter.status.trim()) {
                searchParams.status = contactFilter.status;
            }
            
            if (contactFilter.campaign !== 'all' && contactFilter.campaign?.trim()) {
                searchParams.campaign_id = contactFilter.campaign;
            }
            
            if (contactFilter.search?.trim()) {
                searchParams.search = contactFilter.search;
            }
            
            console.log('📡 Llamando getContacts con parámetros:', searchParams);
            
            const response = await SellerbotService.getContacts(searchParams);
            
            console.log('📥 Respuesta completa de getContacts:', response);
            
            if (response && response.success !== false) {
                const fetchedContacts = response.data?.contacts || [];
                console.log('📋 Contactos extraídos:', fetchedContacts.length);
                setContacts(fetchedContacts);
            } else {
                console.log('❌ Respuesta de getContacts inválida:', response);
                setContacts([]);
            }
            
        } catch (error: any) {
            console.error('❌ Error completo en loadContacts:', error);
            
            if (!silent) {
                setError(language === 'es' ? 'Error al cargar contactos' : 'Error loading contacts');
                setTimeout(() => setError(null), 3000);
            }
            setContacts([]);
        } finally {
            if (!silent) setIsLoadingContacts(false);
        }
    };

    const loadUploadHistory = async (silent = false) => {
        try {
            if (!silent) setIsLoadingHistory(true);
            
            const response = await SellerbotService.getUploadHistory({
                limit: 20,
                offset: 0
            });
            
            if (response && response.success && response.data?.uploads) {
                const formattedHistory: UploadHistory[] = response.data.uploads.map((upload: any) => ({
                    id: upload.id,
                    fileName: upload.filename || upload.fileName,
                    uploadDate: upload.upload_date || upload.uploadDate,
                    contactsCount: upload.total_contacts || upload.contactsCount,
                    validContacts: upload.valid_contacts || upload.validContacts,
                    invalidContacts: (upload.total_contacts || upload.contactsCount) - (upload.valid_contacts || upload.validContacts),
                    duplicatesFound: upload.duplicates_found || upload.duplicatesFound || 0,
                    status: upload.status,
                    errorMessage: upload.error_message || upload.errorMessage,
                    campaignName: upload.campaign_id ? campaigns.find(c => c.id === upload.campaign_id)?.name : undefined,
                    processingTime: upload.processing_time_ms || upload.processingTime,
                    fileSize: upload.file_size || upload.fileSize
                }));
                
                setUploadHistory(formattedHistory);
            } else {
                // Usar datos mock si no hay servicio
                const mockHistory: UploadHistory[] = [
                    {
                        id: '1',
                        fileName: 'contactos_enero.csv',
                        uploadDate: new Date().toISOString(),
                        contactsCount: 150,
                        validContacts: 145,
                        invalidContacts: 5,
                        duplicatesFound: 12,
                        status: 'success',
                        campaignName: 'Campaña Q1 2024'
                    }
                ];
                setUploadHistory(mockHistory);
            }
            
        } catch (error: any) {
            console.error('Error loading upload history:', error);
            if (!silent) {
                setError(language === 'es' ? 'Error al cargar historial' : 'Error loading history');
                setTimeout(() => setError(null), 3000);
            }
        } finally {
            if (!silent) setIsLoadingHistory(false);
        }
    };

    // ==================== 📁 FUNCIONES DE UPLOAD ====================
    
    const handleFileSelect = async (file: File) => {
    try {
        console.log('📁 File selected:', file.name);
        setIsProcessingFile(true);
        setIsValidating(true);
        setUploadProgress(10);
        setError(null);
        setSuccess(null);
        
        // CRÍTICO: Limpiar estados previos
        setShowUploadModal(false);
        setPreviewContacts([]);
        
        // Validar tipo de archivo
        const allowedTypes = ['.csv', '.xlsx', '.xls'];
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        
        if (!allowedTypes.includes(fileExtension)) {
            throw new Error(language === 'es' 
                ? 'Solo se permiten archivos CSV y Excel'
                : 'Only CSV and Excel files are allowed'
            );
        }
        
        console.log('✅ File type valid:', fileExtension);
        setUploadedFile(file);
        setUploadProgress(30);
        
        // Procesar archivo usando la función migrada
        console.log('🔄 Processing file...');
        const processedContacts = await processUploadedFile(file);
        console.log('📋 Contacts processed:', processedContacts.length);
        
        setUploadProgress(80);
        setPreviewContacts(processedContacts);
        
        // Crear validación
        const validation: ContactValidationResult = {
            isValid: true,
            errors: [],
            warnings: processedContacts.length > 50 ? [
                language === 'es' ? 'Solo se procesarán los primeros 50 contactos' : 'Only first 50 contacts will be processed'
            ] : [],
            phoneIssues: [],
            duplicates: [],
            totalContacts: processedContacts.length,
            validContacts: processedContacts.filter(c => !c.hasError).length
        };
        
        setValidationResult(validation);
        setUploadProgress(100);
        
        // CRÍTICO: Limpiar estados de procesamiento ANTES de mostrar modal
        setIsProcessingFile(false);
        setIsValidating(false);
        
        console.log('🚀 Opening upload modal...');
        
        // CRÍTICO: Usar setTimeout para asegurar que el estado se actualiza
        setTimeout(() => {
            setShowUploadModal(true);
            console.log('✅ Modal should be visible now');
        }, 50);
        
        setSuccess(language === 'es' 
            ? `Archivo procesado: ${validation.validContacts} contactos válidos encontrados` 
            : `File processed: ${validation.validContacts} valid contacts found`);
        setTimeout(() => setSuccess(null), 3000);
        
    } catch (error: any) {
        console.error('❌ Error processing file:', error);
        setIsProcessingFile(false);
        setIsValidating(false);
        setUploadProgress(0);
        setShowUploadModal(false); // CRÍTICO: Asegurar que modal se cierra en error
        setError(error.message || (language === 'es' ? 'Error al procesar archivo' : 'Error processing file'));
        setTimeout(() => setError(null), 5000);
    }
};

const handleConfirmUpload = async () => {
    if (previewContacts.length === 0) {
        console.log('❌ No preview contacts to upload');
        return;
    }
    
    console.log('🚀 Starting upload confirmation...');
    
    // Marcar que hay modal abierto y bloquear updates
    preventUpdates.current = true;
    setIsProcessingFile(true);
    setUploadProgress(0);
    
    try {
        setUploadProgress(30);
        
        const contactsData = previewContacts
            .filter(contact => !contact.hasError)
            .slice(0, 50)
            .map(contact => ({
                name: contact.name || '',
                phone: contact.phone || '',
                email: contact.email || '',
                company: contact.company || '',
                product_service_interest: contact.product_service_interest || contact.course || '',
                source: 'csv_upload'
            }));
        
        if (contactsData.length === 0) {
            throw new Error(language === 'es' 
                ? 'No hay contactos válidos para subir' 
                : 'No valid contacts to upload');
        }
        
        console.log('📤 Uploading contacts:', contactsData.length);
        console.log('📋 Sample contact:', contactsData[0]);
        
        setUploadProgress(60);
        
        const response = await SellerbotService.uploadContactsData(contactsData, {
            campaign_id: selectedCampaign && selectedCampaign.trim() ? selectedCampaign.trim() : undefined,
            source: 'csv_upload'
        });
        
        setUploadProgress(100);
        console.log('📥 Upload response:', response);
        
        if (response && (response.success === true || response.success === undefined)) {
            // ✅ CORRECCIÓN 1: Limpiar isProcessingFile INMEDIATAMENTE después del éxito
            setIsProcessingFile(false);
            preventUpdates.current = false;
            
            // Cerrar modales
            setShowUploadModal(false);
            setShowValidationModal(false);
            
            // Limpiar todos los estados relacionados con upload
            setPreviewContacts([]);
            setUploadedFile(null);
            setSelectedCampaign('');
            setSelectedAgent('');
            setValidationResult(null);
            setDuplicatesFound([]);
            
            const savedCount = response.data?.saved_contacts || 
                  response.data?.valid_contacts ||
                  response.data?.total_processed ||
                  (response.data as any)?.contacts_count ||
                  contactsData.length;
            
            const successMsg = language === 'es' 
                ? `✅ ${savedCount} contactos guardados exitosamente`
                : `✅ ${savedCount} contacts saved successfully`;
            
            setSuccess(successMsg);
            setTimeout(() => setSuccess(null), 5000);
            
            // Recargar datos después de un delay
            setTimeout(async () => {
                try {
                    await Promise.all([
                        loadContacts(false), // ✅ CORRECCIÓN 2: false para mostrar loading
                        loadUploadHistory(true)
                    ]);
                } catch (error) {
                    console.log('Silent refresh error:', error);
                }
            }, 1000); // ✅ CORRECCIÓN 3: Reducir delay a 1 segundo
            
        } else {
            throw new Error(response?.message || 'Upload failed');
        }
        
    } catch (error: any) {
        console.error('❌ Upload error:', error);
        
        // ✅ CORRECCIÓN 4: Limpiar TODOS los estados en caso de error
        setIsProcessingFile(false);
        preventUpdates.current = false;
        setUploadProgress(0);
        
        let errorMsg = language === 'es' ? 'Error al subir contactos' : 'Error uploading contacts';
        
        if (error.message?.includes('timeout')) {
            errorMsg = language === 'es' 
                ? 'Timeout: El proceso tomó demasiado tiempo. Intenta con menos contactos.' 
                : 'Timeout: Process took too long. Try with fewer contacts.';
        } else if (error.response?.data?.message) {
            errorMsg = error.response.data.message;
        } else if (error.message) {
            errorMsg = error.message;
        }
        
        setError(errorMsg);
        setTimeout(() => setError(null), 10000);
    }
    // ✅ CORRECCIÓN 5: NO usar finally - todo se maneja arriba
};


// ==================== 🔧 MODAL CORREGIDO CON BACKDROP ESTÁTICO ====================

const UploadContactsModal = () => (
    <Modal 
        show={showUploadModal} 
        onHide={() => setShowUploadModal(false)} // SIMPLIFICADO
        size="xl"
        centered
        scrollable
        backdrop={isProcessingFile ? "static" : true}
        keyboard={!isProcessingFile}
    >
        <Modal.Header closeButton={!isProcessingFile}>
            <Modal.Title>
                <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                <span className="d-none d-sm-inline">
                    {language === 'es' ? 'Confirmar Subida de Contactos' : 'Confirm Contacts Upload'}
                </span>
                <span className="d-inline d-sm-none">
                    {language === 'es' ? 'Subir Contactos' : 'Upload Contacts'}
                </span>
            </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2 p-md-3">
            {/* MOSTRAR CONTENIDO INMEDIATAMENTE SI HAY CONTACTOS */}
            {previewContacts.length > 0 ? (
                <>
                    {/* Información del archivo */}
                    <Alert variant="info" className="mb-3">
                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                            <span className="mb-2 mb-md-0">
                                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                <strong className="small">{uploadedFile?.name}</strong>
                            </span>
                            <Badge bg="success">
                                {previewContacts.filter(c => !c.hasError).length} contactos válidos
                            </Badge>
                        </div>
                    </Alert>

                    {/* Vista previa de contactos */}
                    <div className="mb-3">
                        <h6 className="small">
                            {language === 'es' ? 'Vista Previa de Contactos:' : 'Contacts Preview:'}
                        </h6>
                        <div className="table-responsive" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                            <Table striped size="sm">
                                <thead className="bg-light sticky-top">
                                    <tr>
                                        <th className="small">{language === 'es' ? 'Nombre' : 'Name'}</th>
                                        <th className="small d-none d-md-table-cell">{language === 'es' ? 'Teléfono' : 'Phone'}</th>
                                        <th className="small d-none d-lg-table-cell">{language === 'es' ? 'Email' : 'Email'}</th>
                                        <th className="small">{language === 'es' ? 'Estado' : 'Status'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewContacts.slice(0, 10).map((contact, index) => (
                                        <tr key={index}>
                                            <td className="small">
                                                <div className="d-flex align-items-center">
                                                    {contact.hasError ? (
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-1" />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faCheck} className="text-success me-1" />
                                                    )}
                                                    <div>
                                                        <div>{contact.name}</div>
                                                        <div className="d-block d-md-none text-muted" style={{ fontSize: '0.7rem' }}>
                                                            {contact.phone}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="small d-none d-md-table-cell">
                                                <code style={{ fontSize: '0.7rem' }}>{contact.phone}</code>
                                            </td>
                                            <td className="small d-none d-lg-table-cell">{contact.email || '-'}</td>
                                            <td>
                                                <Badge 
                                                    bg={contact.hasError ? 'warning' : 'success'} 
                                                    className="small"
                                                >
                                                    {contact.hasError ? 'Error' : 'OK'}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>

                    {/* Estadísticas */}
                    <Row className="text-center">
                        <Col xs={6} sm={3}>
                            <div className="border rounded p-2">
                                <h6 className="mb-0 text-primary small">{previewContacts.length}</h6>
                                <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                    {language === 'es' ? 'Total' : 'Total'}
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} sm={3}>
                            <div className="border rounded p-2">
                                <h6 className="mb-0 text-success small">{previewContacts.filter(c => !c.hasError).length}</h6>
                                <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                    {language === 'es' ? 'Válidos' : 'Valid'}
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} sm={3}>
                            <div className="border rounded p-2">
                                <h6 className="mb-0 text-warning small">{previewContacts.filter(c => c.hasError).length}</h6>
                                <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                    {language === 'es' ? 'Errores' : 'Errors'}
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} sm={3}>
                            <div className="border rounded p-2">
                                <h6 className="mb-0 text-info small">{previewContacts.filter(c => c.email).length}</h6>
                                <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                    {language === 'es' ? 'Con email' : 'With email'}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </>
            ) : (
                <div className="text-center py-4">
                    <Spinner animation="border" className="mb-3" />
                    <h5>{language === 'es' ? 'Procesando archivo...' : 'Processing file...'}</h5>
                </div>
            )}
        </Modal.Body>
        <Modal.Footer className="p-2 p-md-3">
            <div className="d-flex flex-column flex-sm-row gap-2 w-100">
                <Button 
                    variant="secondary" 
                    onClick={() => setShowUploadModal(false)} 
                    className="order-2 order-sm-1"
                    size="sm"
                >
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirmUpload}
                    disabled={previewContacts.filter(c => !c.hasError).length === 0}
                    className="order-1 order-sm-2 flex-sm-fill"
                    size="sm"
                >
                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                    {language === 'es' ? 'Confirmar Subida' : 'Confirm Upload'}
                    <Badge bg="light" text="dark" className="ms-2">
                        {previewContacts.filter(c => !c.hasError).length}
                    </Badge>
                </Button>
            </div>
        </Modal.Footer>
    </Modal>
);

// ⚠️ AGREGAR TIMEOUT DE SEGURIDAD PARA RESET AUTOMÁTICO
useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (isProcessingFile) {
        // Reset automático después de 2 minutos si algo sale mal
        timeoutId = setTimeout(() => {
            console.warn('⚠️ Upload timeout reached - forcing reset');
            setIsProcessingFile(false);
            setUploadProgress(0);
            preventUpdates.current = false;
            setError(language === 'es' 
                ? 'Timeout: El proceso tomó demasiado tiempo'
                : 'Timeout: Process took too long');
        }, 120000); // 2 minutos
    }
    
    return () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };
}, [isProcessingFile, language]);

useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (isProcessingFile) {
        // Reset automático después de 2 minutos si algo sale mal
        timeoutId = setTimeout(() => {
            console.warn('⚠️ Upload timeout reached - forcing reset');
            setIsProcessingFile(false);
            setUploadProgress(0);
            preventUpdates.current = false;
            setError(language === 'es' 
                ? 'Timeout: El proceso tomó demasiado tiempo'
                : 'Timeout: Process took too long');
        }, 120000); // 2 minutos
    }
    
    return () => {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    };
}, [isProcessingFile, language]);

// ==================== 🔧 FUNCIÓN DE DEBUG PARA ANALIZAR PROBLEMA ====================

const debugUploadProcess = () => {
    console.log('=== DEBUG UPLOAD PROCESS ===');
    console.log('isProcessingFile:', isProcessingFile);
    console.log('showUploadModal:', showUploadModal);
    console.log('preventUpdates.current:', preventUpdates.current);
    console.log('uploadProgress:', uploadProgress);
    console.log('previewContacts.length:', previewContacts.length);
    console.log('selectedCampaign:', selectedCampaign);
    console.log('selectedAgent:', selectedAgent);
    console.log('============================');
};

    // ==================== 🗑️ FUNCIONES DE GESTIÓN ====================
    
    const handleDeleteContact = async (contactId: string) => {
        if (window.confirm(language === 'es' ? 
            '¿Eliminar este contacto?' : 'Delete this contact?')) {
            try {
                await SellerbotService.deleteContact(contactId);
                await loadContacts();
                setSuccess(language === 'es' ? 'Contacto eliminado' : 'Contact deleted');
                setTimeout(() => setSuccess(null), 3000);
            } catch (error: any) {
                setError(language === 'es' ? 'Error al eliminar contacto' : 'Error deleting contact');
                setTimeout(() => setError(null), 3000);
            }
        }
    };

    // Funciones para drag & drop
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    // ==================== 🎨 FUNCIONES DE UTILIDAD ====================
    
    const getText = (key: string) => {
        const texts: Record<string, Record<string, string>> = {
            es: {
                title: 'Gestión de Contactos',
                uploadContacts: 'Subir Contactos',
                viewContacts: 'Ver Contactos',
                loading: 'Cargando...',
                error: 'Error',
                noContacts: 'No hay contactos registrados',
                noContactsDesc: 'Sube tu primer archivo de contactos para comenzar',
                search: 'Buscar',
                status: 'Estado',
                campaign: 'Campaña',
                agent: 'Agente',
                all: 'Todos',
                new: 'Nuevo',
                contacted: 'Contactado',
                interested: 'Interesado',
                closed: 'Cerrado',
                rejected: 'Rechazado',
                name: 'Nombre',
                phone: 'Teléfono',
                email: 'Email',
                company: 'Empresa',
                actions: 'Acciones',
                edit: 'Editar',
                delete: 'Eliminar',
                uploadHistory: 'Historial de Subidas',
                file: 'Archivo',
                date: 'Fecha',
                contacts: 'Contactos',
                valid: 'Válidos',
                export: 'Exportar',
                clear: 'Limpiar',
                selectFile: 'Seleccionar Archivo',
                dragDrop: 'Arrastra archivos aquí o',
                supportedFormats: 'Formatos: CSV, XLSX',
                lastContact: 'Último Contacto',
                attempts: 'Intentos',
                cardsView: 'Vista Tarjetas',
                tableView: 'Vista Tabla',
                refreshContacts: 'Actualizar contactos',
                validateContacts: 'Validar Contactos',
                confirmUpload: 'Confirmar Subida',
                assignToCampaign: 'Asignar a Campaña',
                assignToAgent: 'Asignar a Agente',
                unassigned: 'Sin asignar',
                contactsPreview: 'Vista Previa de Contactos',
                processingFile: 'Procesando archivo...',
                uploading: 'Subiendo...',
                cancel: 'Cancelar',
                close: 'Cerrar'
            },
            en: {
                title: 'Contact Management',
                uploadContacts: 'Upload Contacts',
                viewContacts: 'View Contacts',
                loading: 'Loading...',
                error: 'Error',
                noContacts: 'No contacts registered',
                noContactsDesc: 'Upload your first contact file to get started',
                search: 'Search',
                status: 'Status',
                campaign: 'Campaign',
                agent: 'Agent',
                all: 'All',
                new: 'New',
                contacted: 'Contacted',
                interested: 'Interested',
                closed: 'Closed',
                rejected: 'Rejected',
                name: 'Name',
                phone: 'Phone',
                email: 'Email',
                company: 'Company',
                actions: 'Actions',
                edit: 'Edit',
                delete: 'Delete',
                uploadHistory: 'Upload History',
                file: 'File',
                date: 'Date',
                contacts: 'Contacts',
                valid: 'Valid',
                export: 'Export',
                clear: 'Clear',
                selectFile: 'Select File',
                dragDrop: 'Drag files here or',
                supportedFormats: 'Formats: CSV, XLSX',
                lastContact: 'Last Contact',
                attempts: 'Attempts',
                cardsView: 'Cards View',
                tableView: 'Table View',
                refreshContacts: 'Refresh contacts',
                validateContacts: 'Validate Contacts',
                confirmUpload: 'Confirm Upload',
                assignToCampaign: 'Assign to Campaign',
                assignToAgent: 'Assign to Agent',
                unassigned: 'Unassigned',
                contactsPreview: 'Contacts Preview',
                processingFile: 'Processing file...',
                uploading: 'Uploading...',
                cancel: 'Cancel',
                close: 'Close'
            }
        };
        return texts[language]?.[key] || key;
    };

    const getStatusColor = (status: string) => {
        const colors = {
            new: 'secondary',
            contacted: 'info',
            interested: 'warning',
            closed: 'success',
            rejected: 'danger'
        };
        return colors[status as keyof typeof colors] || 'secondary';
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US');
    };

    // Filtrar contactos
    const filteredContacts = contacts.filter(contact => {
        // Filtro de búsqueda
        if (contactFilter.search) {
            const searchLower = contactFilter.search.toLowerCase();
            const matchesSearch = 
                contact.name?.toLowerCase().includes(searchLower) ||
                contact.phone?.toLowerCase().includes(searchLower) ||
                contact.email?.toLowerCase().includes(searchLower) ||
                contact.company?.toLowerCase().includes(searchLower);
            if (!matchesSearch) return false;
        }

        // Filtro de estado
        if (contactFilter.status !== 'all' && contact.status !== contactFilter.status) {
            return false;
        }

        // Filtro de campaña
        if (contactFilter.campaign !== 'all' && contact.campaign_id !== contactFilter.campaign) {
            return false;
        }

        return true;
    });

    // ==================== 🎯 COMPONENTES DE RENDERIZADO ====================

    // Componente de Card de Contacto
    const ContactCard = ({ contact, index }: { contact: Contact; index: number }) => (
        <Card className="contact-card mb-3 h-100">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6 className="mb-1">{contact.name}</h6>
                    <Badge bg={getStatusColor(contact.status)}>
                        {getText(contact.status)}
                    </Badge>
                </div>
                
                <div className="small text-muted mb-2">
                    <div className="mb-1">
                        <FontAwesomeIcon icon={faPhone} className="me-1" />
                        {contact.phone}
                    </div>
                    {contact.email && (
                        <div className="mb-1">
                            <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                            {contact.email}
                        </div>
                    )}
                    {contact.company && (
                        <div className="mb-1">
                            <FontAwesomeIcon icon={faBuilding} className="me-1" />
                            {contact.company}
                        </div>
                    )}
                </div>

                {contact.product_service_interest && (
                    <div className="small mb-2">
                        <strong>{language === 'es' ? 'Interés:' : 'Interest:'}</strong> {contact.product_service_interest}
                    </div>
                )}

                <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                        {contact.last_contact_date ? formatDate(contact.last_contact_date) : getText('new')}
                    </small>
                    
                    <Dropdown align="end">
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <FontAwesomeIcon icon={faCog} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item>
                                <FontAwesomeIcon icon={faEye} className="me-2" />
                                {language === 'es' ? 'Ver Conversación' : 'View Conversation'}
                            </Dropdown.Item>
                            <Dropdown.Item>
                                <FontAwesomeIcon icon={faEdit} className="me-2" />
                                {getText('edit')}
                            </Dropdown.Item>
                            <Dropdown.Item>
                                <FontAwesomeIcon icon={faPhone} className="me-2" />
                                {language === 'es' ? 'Contactar Ahora' : 'Contact Now'}
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item 
                                className="text-danger"
                                onClick={() => contact.id && handleDeleteContact(contact.id)}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                {getText('delete')}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </Card.Body>
        </Card>
    );

    // ==================== 📊 COMPONENTE DE HISTORIAL DE UPLOADS ====================

    const UploadHistoryTable = () => {
        const [currentPage, setCurrentPage] = useState(1);
        const itemsPerPage = 10;
        
        // Calcular paginación
        const totalPages = Math.ceil(uploadHistory.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentItems = uploadHistory.slice(startIndex, endIndex);

        // Función para eliminar registro del historial
        const handleDeleteUploadHistory = async (uploadId: string) => {
            try {
                const confirmed = window.confirm(
                    language === 'es' 
                        ? '¿Está seguro de eliminar este registro del historial?' 
                        : 'Are you sure you want to delete this history record?'
                );
                
                if (!confirmed) return;

                console.log('🗑️ Deleting upload history:', uploadId);
                
                // Eliminar del estado local inmediatamente (optimistic update)
                setUploadHistory(prevHistory => 
                    prevHistory.filter(upload => upload.id !== uploadId)
                );
                
                // Luego intentar eliminar del backend si existe el método
                try {
                    if (SellerbotService.deleteUploadHistory) {
                        await SellerbotService.deleteUploadHistory(uploadId, false);
                    }
                } catch (backendError) {
                    // Si falla el backend, revertir el cambio
                    await loadUploadHistory(true);
                    throw backendError;
                }
                
                setSuccess(language === 'es' 
                    ? 'Registro eliminado del historial'
                    : 'Record deleted from history');
                setTimeout(() => setSuccess(null), 3000);
                
            } catch (error: any) {
                console.error('❌ Error deleting upload history:', error);
                setError(language === 'es' 
                    ? 'Error al eliminar registro: ' + error.message
                    : 'Error deleting record: ' + error.message);
                setTimeout(() => setError(null), 5000);
            }
        };

        // Función para ver detalles de upload
        const handleViewUploadDetails = (upload: any) => {
            console.log('📋 Viewing upload details:', upload);
            
            const details = `
📁 ${language === 'es' ? 'Archivo:' : 'File:'} ${upload.fileName}
📅 ${language === 'es' ? 'Fecha:' : 'Date:'} ${formatDate(upload.uploadDate)}
📊 ${language === 'es' ? 'Contactos:' : 'Contacts:'} ${upload.validContacts}/${upload.contactsCount}
${upload.duplicatesFound > 0 ? `🔄 ${language === 'es' ? 'Duplicados:' : 'Duplicates:'} ${upload.duplicatesFound}` : ''}
${upload.errorMessage ? `❌ ${language === 'es' ? 'Error:' : 'Error:'} ${upload.errorMessage}` : ''}
${upload.processingTime ? `⏱️ ${language === 'es' ? 'Tiempo:' : 'Time:'} ${upload.processingTime}ms` : ''}
${upload.fileSize ? `💾 ${language === 'es' ? 'Tamaño:' : 'Size:'} ${(upload.fileSize / 1024).toFixed(1)} KB` : ''}
`;
            
            alert(details);
            
            setSuccess(language === 'es' 
                ? 'Detalles mostrados'
                : 'Details shown');
            setTimeout(() => setSuccess(null), 2000);
        };

        // Función para exportar historial
        const handleExportHistory = () => {
            try {
                console.log('📤 Exporting upload history...');
                
                if (uploadHistory.length === 0) {
                    setError(language === 'es' 
                        ? 'No hay historial para exportar'
                        : 'No history to export');
                    setTimeout(() => setError(null), 3000);
                    return;
                }
                
                // Crear CSV del historial
                const headers = [
                    language === 'es' ? 'Archivo' : 'File',
                    language === 'es' ? 'Fecha' : 'Date', 
                    language === 'es' ? 'Total Contactos' : 'Total Contacts',
                    language === 'es' ? 'Válidos' : 'Valid',
                    language === 'es' ? 'Errores' : 'Errors',
                    language === 'es' ? 'Estado' : 'Status',
                    language === 'es' ? 'Campaña' : 'Campaign'
                ];
                const csvData = uploadHistory.map(upload => [
                    upload.fileName,
                    formatDate(upload.uploadDate),
                    upload.contactsCount,
                    upload.validContacts,
                    upload.invalidContacts,
                    upload.status,
                    upload.campaignName || (language === 'es' ? 'Sin campaña' : 'No campaign')
                ]);
                
                const csvContent = [
                    headers.join(','),
                    ...csvData.map(row => row.join(','))
                ].join('\n');
                
                // Descargar archivo
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `upload-history-${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setSuccess(language === 'es' 
                    ? 'Historial exportado exitosamente'
                    : 'History exported successfully');
                setTimeout(() => setSuccess(null), 3000);
                
            } catch (error: any) {
                console.error('❌ Error exporting history:', error);
                setError(language === 'es' 
                    ? 'Error al exportar historial'
                    : 'Error exporting history');
                setTimeout(() => setError(null), 3000);
            }
        };

        // Función para limpiar todo el historial
        const handleClearAllHistory = async () => {
            try {
                const confirmed = window.confirm(
                    language === 'es' 
                        ? '¿Está seguro de eliminar TODO el historial? Esta acción no se puede deshacer.'
                        : 'Are you sure you want to delete ALL history? This action cannot be undone.'
                );
                
                if (!confirmed) return;
                
                console.log('🧹 Clearing all upload history...');
                
                // Limpiar estado local inmediatamente
                setUploadHistory([]);
                
                // Intentar eliminar del backend si existe el método
                try {
                    if (SellerbotService.deleteUploadHistory) {
                        const deletePromises = uploadHistory.map(upload => 
                            SellerbotService.deleteUploadHistory(upload.id, false).catch(err => {
                                console.warn(`Failed to delete ${upload.id}:`, err);
                            })
                        );
                        
                        await Promise.allSettled(deletePromises);
                    }
                } catch (backendError) {
                    console.warn('Backend deletion failed, but local state cleared:', backendError);
                }
                
                setSuccess(language === 'es' 
                    ? 'Todo el historial ha sido eliminado'
                    : 'All history has been cleared');
                setTimeout(() => setSuccess(null), 3000);
                
            } catch (error: any) {
                console.error('❌ Error clearing history:', error);
                setError(language === 'es' 
                    ? 'Error al limpiar historial'
                    : 'Error clearing history');
                setTimeout(() => setError(null), 3000);
            }
        };

        return (
            <Card className="mt-4">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <FontAwesomeIcon icon={faHistory} className="me-2" />
                            {language === 'es' ? 'Historial de Archivos Subidos' : 'Upload History'}
                            <Badge bg="secondary" className="ms-2">{uploadHistory.length}</Badge>
                        </h5>
                        <div className="d-flex gap-2">
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => loadUploadHistory(false)}
                                disabled={isLoadingHistory}
                            >
                                <FontAwesomeIcon icon={faSync} spin={isLoadingHistory} className="me-1" />
                                {language === 'es' ? 'Actualizar' : 'Refresh'}
                            </Button>
                            {uploadHistory.length > 0 && (
                                <>
                                    <Button 
                                        variant="outline-info" 
                                        size="sm"
                                        onClick={handleExportHistory}
                                    >
                                        <FontAwesomeIcon icon={faDownload} className="me-1" />
                                        {language === 'es' ? 'Exportar' : 'Export'}
                                    </Button>
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={handleClearAllHistory}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} className="me-1" />
                                        {language === 'es' ? 'Limpiar Todo' : 'Clear All'}
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {isLoadingHistory ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                            <p className="mt-2 mb-0 small">
                                {language === 'es' ? 'Cargando historial...' : 'Loading history...'}
                            </p>
                        </div>
                    ) : uploadHistory.length === 0 ? (
                        <div className="text-center py-4">
                            <FontAwesomeIcon icon={faHistory} size="2x" className="text-muted mb-2" />
                            <p className="text-muted mb-0">
                                {language === 'es' ? 'No hay archivos subidos aún' : 'No files uploaded yet'}
                            </p>
                            <small className="text-muted">
                                {language === 'es' 
                                    ? 'El historial aparecerá aquí después de subir contactos'
                                    : 'History will appear here after uploading contacts'
                                }
                            </small>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="border-0 fw-semibold small">{language === 'es' ? 'Archivo' : 'File'}</th>
                                            <th className="border-0 fw-semibold small d-none d-md-table-cell">{language === 'es' ? 'Fecha' : 'Date'}</th>
                                            <th className="border-0 fw-semibold small">{language === 'es' ? 'Contactos' : 'Contacts'}</th>
                                            <th className="border-0 fw-semibold small">{language === 'es' ? 'Estado' : 'Status'}</th>
                                            <th className="border-0 fw-semibold small d-none d-lg-table-cell">{language === 'es' ? 'Campaña' : 'Campaign'}</th>
                                            <th className="border-0 fw-semibold small">{language === 'es' ? 'Acciones' : 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((upload) => (
                                            <tr key={upload.id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon 
                                                            icon={faFileUpload} 
                                                            className="me-2 text-primary" 
                                                        />
                                                        <div>
                                                            <strong className="small">{upload.fileName}</strong>
                                                            {upload.fileSize && (
                                                                <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                    {(upload.fileSize / 1024).toFixed(1)} KB
                                                                </div>
                                                            )}
                                                            {/* Mostrar fecha en móviles */}
                                                            <div className="d-block d-md-none text-muted" style={{ fontSize: '0.7rem' }}>
                                                                {formatDate(upload.uploadDate)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="small text-muted d-none d-md-table-cell">
                                                    {formatDate(upload.uploadDate)}
                                                    {upload.processingTime && (
                                                        <div style={{ fontSize: '0.7rem' }}>
                                                            {upload.processingTime}ms
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <div className="d-flex gap-1 mb-1">
                                                            <Badge bg="success" className="small">
                                                                {upload.validContacts}
                                                            </Badge>
                                                            {upload.invalidContacts > 0 && (
                                                                <Badge bg="warning" className="small">
                                                                    {upload.invalidContacts}
                                                                </Badge>
                                                            )}
                                                            {upload.duplicatesFound > 0 && (
                                                                <Badge bg="info" className="small">
                                                                    {upload.duplicatesFound}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <small className="text-muted">
                                                            {language === 'es' ? 'Total:' : 'Total:'} {upload.contactsCount}
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge 
                                                        bg={upload.status === 'success' ? 'success' : 
                                                            upload.status === 'error' ? 'danger' : 'warning'}
                                                        className="small"
                                                    >
                                                        {upload.status === 'success' ? 
                                                            (language === 'es' ? 'Éxito' : 'Success') :
                                                            upload.status === 'error' ?
                                                            (language === 'es' ? 'Error' : 'Error') :
                                                            (language === 'es' ? 'Parcial' : 'Partial')
                                                        }
                                                    </Badge>
                                                    {upload.errorMessage && (
                                                        <div className="d-block d-lg-none">
                                                            <small className="text-danger" style={{ fontSize: '0.7rem' }}>
                                                                {upload.errorMessage.substring(0, 30)}...
                                                            </small>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="small text-muted d-none d-lg-table-cell">
                                                    {upload.campaignName || (
                                                        <span className="text-muted">
                                                            {language === 'es' ? 'Sin asignar' : 'Unassigned'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Dropdown align="end">
                                                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                            <FontAwesomeIcon icon={faCog} />
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu>
                                                            <Dropdown.Item onClick={() => handleViewUploadDetails(upload)}>
                                                                <FontAwesomeIcon icon={faEye} className="me-2" />
                                                                {language === 'es' ? 'Ver Detalles' : 'View Details'}
                                                            </Dropdown.Item>
                                                            <Dropdown.Item>
                                                                <FontAwesomeIcon icon={faDownload} className="me-2" />
                                                                {language === 'es' ? 'Descargar' : 'Download'}
                                                            </Dropdown.Item>
                                                            {upload.status === 'error' && (
                                                                <Dropdown.Item>
                                                                    <FontAwesomeIcon icon={faSync} className="me-2" />
                                                                    {language === 'es' ? 'Reprocesar' : 'Reprocess'}
                                                                </Dropdown.Item>
                                                            )}
                                                            <Dropdown.Divider />
                                                            <Dropdown.Item 
                                                                className="text-danger"
                                                                onClick={() => handleDeleteUploadHistory(upload.id)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                                                {language === 'es' ? 'Eliminar' : 'Delete'}
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            
                            {/* Paginación */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <small className="text-muted">
                                        {language === 'es' 
                                            ? `Mostrando ${startIndex + 1}-${Math.min(endIndex, uploadHistory.length)} de ${uploadHistory.length} registros`
                                            : `Showing ${startIndex + 1}-${Math.min(endIndex, uploadHistory.length)} of ${uploadHistory.length} records`
                                        }
                                    </small>
                                    <div className="d-flex gap-1">
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm" 
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                        >
                                            <FontAwesomeIcon icon={faChevronLeft} />
                                        </Button>
                                        
                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .filter(page => 
                                                page === 1 || 
                                                page === totalPages || 
                                                Math.abs(page - currentPage) <= 1
                                            )
                                            .map((page, index, array) => (
                                                <React.Fragment key={page}>
                                                    {index > 0 && array[index - 1] !== page - 1 && (
                                                        <span className="px-2 py-1 small text-muted">...</span>
                                                    )}
                                                    <Button
                                                        variant={currentPage === page ? 'primary' : 'outline-secondary'}
                                                        size="sm"
                                                        onClick={() => setCurrentPage(page)}
                                                    >
                                                        {page}
                                                    </Button>
                                                </React.Fragment>
                                            ))
                                        }
                                        
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm" 
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                        >
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
                <Card.Footer className="bg-transparent">
                    <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                            {language === 'es' 
                                ? 'Historial de uploads de contactos'
                                : 'Contact upload history'
                            }
                        </small>
                        <div className="d-flex gap-1">
                            <Badge bg="success" className="small">
                                {language === 'es' ? 'Válidos' : 'Valid'}
                            </Badge>
                            <Badge bg="warning" className="small">
                                {language === 'es' ? 'Errores' : 'Errors'}
                            </Badge>
                            <Badge bg="info" className="small">
                                {language === 'es' ? 'Duplicados' : 'Duplicates'}
                            </Badge>
                        </div>
                    </div>
                </Card.Footer>
            </Card>
        );
    };

    // ==================== 🎯 RENDERIZADO PRINCIPAL ====================
    
    return (
        <div className="contacts-tab">
            <style>{customStyles}</style>
            
            {/* Mensajes de estado */}
            {error && (
                <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                </Alert>
            )}
            
            {success && (
                <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccess(null)}>
                    <FontAwesomeIcon icon={faCheck} className="me-2" />
                    {success}
                </Alert>
            )}

            {/* Header con acciones principales */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>
                            <FontAwesomeIcon icon={faUsers} className="me-2" />
                            {getText('title')}
                            <Badge bg="primary" className="ms-2">{filteredContacts.length}</Badge>
                        </h4>
                        <div className="d-flex gap-2 align-items-center">
                            {/* Botones de vista */}
                            <div className="btn-group me-2" role="group">
                                <Button
                                    variant={viewMode === 'cards' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setViewMode('cards')}
                                    title={getText('cardsView')}
                                >
                                    <FontAwesomeIcon icon={faTh} />
                                </Button>
                                <Button
                                    variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setViewMode('table')}
                                    title={getText('tableView')}
                                >
                                    <FontAwesomeIcon icon={faList} />
                                </Button>
                            </div>
                            
                            <Button 
                                variant="success"
                                size="sm"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={isProcessingFile}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <FontAwesomeIcon icon={faFileUpload} className="me-1" />
                                {getText('uploadContacts')}
                            </Button>
                            
                            <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => loadContacts()}
                                disabled={isLoadingContacts}
                                title={getText('refreshContacts')}
                            >
                                <FontAwesomeIcon icon={faSync} spin={isLoadingContacts} />
                            </Button>
                        </div>
                    </div>

                    {/* Filtros y búsqueda */}
                    <Row className="mb-3">
                        <Col xs={12} md={4} className="mb-2 mb-md-0">
                            <InputGroup size="sm">
                                <InputGroup.Text>
                                    <FontAwesomeIcon icon={faSearch} />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder={language === 'es' ? 'Buscar contactos...' : 'Search contacts...'}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col xs={6} md={3} className="mb-2 mb-md-0">
                            <Form.Select 
                                size="sm"
                                value={contactFilter.status} 
                                onChange={(e) => setContactFilter(prev => ({ ...prev, status: e.target.value }))}
                            >
                                <option value="all">{getText('all')} - {getText('status')}</option>
                                <option value="new">{getText('new')}</option>
                                <option value="contacted">{getText('contacted')}</option>
                                <option value="interested">{getText('interested')}</option>
                                <option value="closed">{getText('closed')}</option>
                                <option value="rejected">{getText('rejected')}</option>
                            </Form.Select>
                        </Col>
                        <Col xs={6} md={3} className="mb-2 mb-md-0">
                            <Form.Select 
                                size="sm"
                                value={contactFilter.campaign} 
                                onChange={(e) => setContactFilter(prev => ({ ...prev, campaign: e.target.value }))}
                            >
                                <option value="all">{getText('all')} - {getText('campaign')}</option>
                                {campaigns.map(campaign => (
                                    <option key={campaign.id} value={campaign.id}>
                                        {campaign.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={2}>
                            <small className="text-muted">
                                {language === 'es' ? 'Total:' : 'Total:'} {filteredContacts.length}
                            </small>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* Input file oculto */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        handleFileSelect(file);
                    }
                }}
            />

            {/* Área principal de contenido */}
            {isLoadingContacts ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-2">{getText('loading')}</p>
                </div>
            ) : filteredContacts.length === 0 ? (
                <Card className="text-center py-5">
                    <Card.Body>
                        <FontAwesomeIcon icon={faUsers} size="3x" className="text-muted mb-3" />
                        <h5>{getText('noContacts')}</h5>
                        <p className="text-muted mb-4">{getText('noContactsDesc')}</p>
                        <Button 
                            variant="primary"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                            {getText('uploadContacts')}
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    {/* Vista Cards */}
                    {viewMode === 'cards' && (
                        <Row>
                            {filteredContacts.map((contact, index) => (
                                <Col key={contact.id || index} xs={12} sm={6} lg={4} xl={3} className="mb-3">
                                    <ContactCard contact={contact} index={index} />
                                </Col>
                            ))}
                        </Row>
                    )}

                    {/* Vista Table */}
                    {viewMode === 'table' && (
                        <Card>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table striped hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="border-0 fw-semibold small">{getText('name')}</th>
                                                <th className="border-0 fw-semibold small d-none d-md-table-cell">{getText('phone')}</th>
                                                <th className="border-0 fw-semibold small d-none d-lg-table-cell">{getText('email')}</th>
                                                <th className="border-0 fw-semibold small d-none d-xl-table-cell">{getText('company')}</th>
                                                <th className="border-0 fw-semibold small">{getText('status')}</th>
                                                <th className="border-0 fw-semibold small d-none d-lg-table-cell">{getText('attempts')}</th>
                                                <th className="border-0 fw-semibold small d-none d-xl-table-cell">{getText('lastContact')}</th>
                                                <th className="border-0 fw-semibold small">{getText('actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredContacts.map((contact, index) => (
                                                <tr key={contact.id || index}>
                                                    <td>
                                                        <div>
                                                            <strong>{contact.name}</strong>
                                                            <div className="d-block d-md-none">
                                                                <small className="text-muted">{contact.phone}</small>
                                                            </div>
                                                            {contact.product_service_interest && (
                                                                <div className="d-block d-lg-none">
                                                                    <small className="text-muted">{contact.product_service_interest}</small>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="d-none d-md-table-cell">
                                                        <code className="small">{contact.phone}</code>
                                                    </td>
                                                    <td className="d-none d-lg-table-cell">
                                                        {contact.email || '-'}
                                                    </td>
                                                    <td className="d-none d-xl-table-cell">
                                                        {contact.company || '-'}
                                                    </td>
                                                    <td>
                                                        <Badge bg={getStatusColor(contact.status)} className="small">
                                                            {getText(contact.status)}
                                                        </Badge>
                                                    </td>
                                                    <td className="d-none d-lg-table-cell">
                                                        <span className="small">{contact.attempts_count || 0}</span>
                                                    </td>
                                                    <td className="d-none d-xl-table-cell">
                                                        <span className="small">
                                                            {contact.last_contact_date ? formatDate(contact.last_contact_date) : '-'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <Dropdown align="end">
                                                            <Dropdown.Toggle variant="outline-secondary" size="sm">
                                                                <FontAwesomeIcon icon={faCog} />
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                                <Dropdown.Item>
                                                                    <FontAwesomeIcon icon={faEye} className="me-2" />
                                                                        {language === 'es' ? 'Ver Conversación' : 'View Conversation'}
                                                                </Dropdown.Item>
                                                                <Dropdown.Item>
                                                                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                                    {getText('edit')}
                                                                </Dropdown.Item>
                                                                <Dropdown.Item>
                                                                    <FontAwesomeIcon icon={faPhone} className="me-2" />
                                                                    {language === 'es' ? 'Contactar Ahora' : 'Contact Now'}
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item
                                                                    className="text-danger"
                                                                    onClick={() => contact.id && handleDeleteContact(contact.id)}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                                                    {getText('delete')}
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Historial de uploads */}
                    <UploadHistoryTable />
                </>
            )}

            {/* ==================== MODALES ==================== */}

            {/* Modal de Upload de Contactos */}
            <Modal 
                show={showUploadModal} 
                onHide={() => setShowUploadModal(false)}
                size="xl"
                centered
                scrollable
                backdrop="static"
            >
                <Modal.Header closeButton={!isProcessingFile}>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                        <span className="d-none d-sm-inline">
                            {language === 'es' ? 'Confirmar Subida de Contactos' : 'Confirm Contacts Upload'}
                        </span>
                        <span className="d-inline d-sm-none">
                            {language === 'es' ? 'Subir Contactos' : 'Upload Contacts'}
                        </span>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-2 p-md-3">
                    {isProcessingFile ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" className="mb-3" />
                            <h5 className="d-none d-md-block">
                                {language === 'es' ? 'Subiendo contactos...' : 'Uploading contacts...'}
                            </h5>
                            <div className="d-block d-md-none small">
                                {language === 'es' ? 'Subiendo...' : 'Uploading...'}
                            </div>
                            <ProgressBar 
                                now={uploadProgress} 
                                label={`${uploadProgress}%`}
                                className="mb-2"
                                style={{ maxWidth: '300px', margin: '0 auto' }}
                            />
                        </div>
                    ) : (
                        <>
                            {/* Información del archivo */}
                            <Alert variant="info" className="mb-3">
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                                    <span className="mb-2 mb-md-0">
                                        <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                        <strong className="small">{uploadedFile?.name}</strong>
                                    </span>
                                    <Badge bg="success">
                                        {previewContacts.filter(c => !c.hasError).length} contactos válidos
                                    </Badge>
                                </div>
                            </Alert>

                            {/* Selección de campaña y agente */}
                            <Row className="mb-4">
                                <Col xs={12} md={6} className="mb-3 mb-md-0">
                                    <Form.Group>
                                        <Form.Label className="small">
                                            <FontAwesomeIcon icon={faUserTie} className="me-2" />
                                            {getText('assignToAgent')}
                                        </Form.Label>
                                        <Form.Select 
                                            value={selectedAgent} 
                                            onChange={(e) => setSelectedAgent(e.target.value)}
                                            size="sm"
                                        >
                                            <option value="">{getText('unassigned')}</option>
                                            {profiles.map(profile => (
                                                <option key={profile.id} value={profile.id}>
                                                    {profile.name} ({profile.personality})
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Label className="small">
                                            <FontAwesomeIcon icon={faBullseye} className="me-2" />
                                            {getText('assignToCampaign')}
                                        </Form.Label>
                                        <Form.Select 
    value={selectedCampaign} 
    onChange={(e) => setSelectedCampaign(e.target.value)}
    size="sm"
    disabled={isProcessingFile} // ✅ CORRECCIÓN 12: Deshabilitar durante procesamiento
>
                                            <option value="">{getText('unassigned')}</option>
                                            {campaigns.map(campaign => (
                                                <option key={campaign.id} value={campaign.id}>
                                                    {campaign.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Vista previa de contactos */}
                            <div className="mb-3">
                                <h6 className="small">
                                    {getText('contactsPreview')}:
                                </h6>
                                <div className="table-responsive" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                    <Table striped size="sm">
                                        <thead className="bg-light sticky-top">
                                            <tr>
                                                <th className="small">{getText('name')}</th>
                                                <th className="small d-none d-md-table-cell">{getText('phone')}</th>
                                                <th className="small d-none d-lg-table-cell">{getText('email')}</th>
                                                <th className="small">{language === 'es' ? 'Estado' : 'Status'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewContacts.slice(0, 10).map((contact, index) => (
                                                <tr key={index}>
                                                    <td className="small">
                                                        <div className="d-flex align-items-center">
                                                            {contact.hasError ? (
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-1" />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faCheck} className="text-success me-1" />
                                                            )}
                                                            <div>
                                                                <div>{contact.name}</div>
                                                                <div className="d-block d-md-none text-muted" style={{ fontSize: '0.7rem' }}>
                                                                    {contact.phone}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="small d-none d-md-table-cell">
                                                        <code style={{ fontSize: '0.7rem' }}>{contact.phone}</code>
                                                    </td>
                                                    <td className="small d-none d-lg-table-cell">{contact.email || '-'}</td>
                                                    <td>
                                                        <Badge 
                                                            bg={contact.hasError ? 'warning' : 'success'} 
                                                            className="small"
                                                        >
                                                            {contact.hasError ? 'Error' : 'OK'}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    {previewContacts.length > 10 && (
                                        <div className="text-center text-muted py-2 small">
                                            {language === 'es' 
                                                ? `... y ${previewContacts.length - 10} contactos más`
                                                : `... and ${previewContacts.length - 10} more contacts`
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Estadísticas */}
                            <Row className="text-center">
                                <Col xs={6} sm={3}>
                                    <div className="border rounded p-2">
                                        <h6 className="mb-0 text-primary small">{previewContacts.length}</h6>
                                        <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                            {language === 'es' ? 'Total' : 'Total'}
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={6} sm={3}>
                                    <div className="border rounded p-2">
                                        <h6 className="mb-0 text-success small">{previewContacts.filter(c => !c.hasError).length}</h6>
                                        <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                            {language === 'es' ? 'Válidos' : 'Valid'}
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={6} sm={3}>
                                    <div className="border rounded p-2">
                                        <h6 className="mb-0 text-warning small">{previewContacts.filter(c => c.hasError).length}</h6>
                                        <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                            {language === 'es' ? 'Errores' : 'Errors'}
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={6} sm={3}>
                                    <div className="border rounded p-2">
                                        <h6 className="mb-0 text-info small">{previewContacts.filter(c => c.email).length}</h6>
                                        <div style={{ fontSize: '0.7rem' }} className="text-muted">
                                            {language === 'es' ? 'Con email' : 'With email'}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-2 p-md-3">
                    <div className="d-flex flex-column flex-sm-row gap-2 w-100">
                        <Button 
                            variant="secondary" 
                            onClick={() => setShowUploadModal(false)} 
                            disabled={isProcessingFile}
                            className="order-2 order-sm-1"
                            size="sm"
                        >
                            {getText('cancel')}
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleConfirmUpload}
                            disabled={isProcessingFile || previewContacts.filter(c => !c.hasError).length === 0}
                            className="order-1 order-sm-2 flex-sm-fill"
                            size="sm"
                        >
                            <FontAwesomeIcon icon={faUpload} className="me-2" />
                            {isProcessingFile 
                                ? getText('uploading')
                                : getText('confirmUpload')
                            }
                            {!isProcessingFile && (
                                <Badge bg="light" text="dark" className="ms-2">
                                    {previewContacts.filter(c => !c.hasError).length}
                                </Badge>
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Zona de Drop para archivos (overlay) */}
            {isDragOver && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        border: '3px dashed #007bff',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column'
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOver(false);
                        const files = Array.from(e.dataTransfer.files);
                        if (files.length > 0) {
                            handleFileSelect(files[0]);
                        }
                    }}
                >
                    <FontAwesomeIcon icon={faCloudUploadAlt} size="4x" className="text-primary mb-3" />
                    <h4 className="text-primary">
                        {language === 'es' ? 'Suelta el archivo aquí' : 'Drop file here'}
                    </h4>
                    <p className="text-muted">
                        {getText('supportedFormats')}
                    </p>
                </div>
            )}

            {/* Event listeners para drag and drop global */}
            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                }}
                onDragLeave={(e) => {
                    // Solo ocultar si realmente salimos del área
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                        setIsDragOver(false);
                    }
                }}
                style={{ minHeight: '100vh', width: '100%', position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
            />
        </div>
    );
};

export default ContactsTab;

/**
 * ContactsTab.tsx v1.0 - Versión Funcional Completa
 * 
 * Características implementadas:
 * ✅ Procesamiento completo de archivos CSV/Excel
 * ✅ Funciones auxiliares migradas de AISeller.tsx
 * ✅ Validación y limpieza de números telefónicos
 * ✅ Detección automática de separadores CSV
 * ✅ Preview de contactos antes del upload
 * ✅ Upload con progreso y validación
 * ✅ Historial de uploads con paginación
 * ✅ Filtros y búsqueda de contactos
 * ✅ Vista de tarjetas y tabla
 * ✅ Drag & drop de archivos
 * ✅ Responsive design
 * ✅ Manejo de errores y estados de carga
 * ✅ Integración con servicios existentes
 * ✅ Multiidioma (ES/EN)
 * 
 * Funciones migradas de AISeller.tsx:
 * - processUploadedFile() - Función principal de procesamiento
 * - detectSeparator() - Detección de separadores CSV
 * - parseCSVLine() - Parser de líneas CSV
 * - cleanPhoneNumber() - Limpieza de teléfonos
 * - detectIfHasHeaders() - Detección de headers
 * - cleanEmailValue() - Limpieza de emails
 * 
 * Estados y tipos migrados:
 * - ContactValidationResult interface
 * - PhoneIssue interface
 * - Estados de validación y duplicados
 * - Estados de drag & drop
 * 
 * Modales implementados:
 * - Modal de upload con preview completo
 * - Overlay de drag & drop
 * - Historial con acciones CRUD
 * 
 * @version 1.0
 * @author AI Seller Team
 * @date 2024
 */